import { Crop } from 'lucide-react'
import {
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import type { CoverControls } from '~/features/shared/components/images-section'
import {
  COVER_ASPECT,
  COVER_MAX_ZOOM,
  type CoverCrop,
  coverCropBox,
  coverOutputSize,
  dragCoverCrop,
  INITIAL_COVER_CROP,
} from '~/features/shared/lib/cover-crop'
import * as m from '~/paraglide/messages'
import { Button } from '~/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/ui/dialog'
import { Slider } from '~/shared/ui/slider'

const KEY_SHARE = 0.02

interface LoadedPicture {
  image: HTMLImageElement
  url: string
}

async function loadPicture(source: File | string): Promise<LoadedPicture> {
  // Through a blob so a saved picture on another host cannot taint the canvas it is drawn on.
  const blob = source instanceof Blob ? source : await (await fetch(source)).blob()
  const url = URL.createObjectURL(blob)
  const image = new Image()
  await new Promise((resolve, reject) => {
    image.onload = resolve
    image.onerror = reject
    image.src = url
  }).catch((error) => {
    URL.revokeObjectURL(url)
    throw error
  })
  return { image, url }
}

async function croppedCover(image: HTMLImageElement, crop: CoverCrop): Promise<File> {
  const natural = { width: image.naturalWidth, height: image.naturalHeight }
  const box = coverCropBox(natural, crop)
  const output = coverOutputSize(box)
  const canvas = document.createElement('canvas')
  canvas.width = output.width
  canvas.height = output.height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('No canvas context')
  context.fillStyle = 'white'
  context.fillRect(0, 0, output.width, output.height)
  context.drawImage(
    image,
    box.left,
    box.top,
    box.width,
    box.height,
    0,
    0,
    output.width,
    output.height,
  )
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.9),
  )
  if (!blob) throw new Error('Could not encode the cover')
  return new File([blob], 'cover.jpg', { type: 'image/jpeg' })
}

/** Lets the person drag and zoom a picture to choose what the cover format keeps of it. */
export function CoverAdjustDialog({
  source,
  onApply,
  onClose,
}: {
  source: File | string
  onApply: (file: File) => void
  onClose: () => void
}) {
  const frameRef = useRef<HTMLButtonElement>(null)
  const sourceRef = useRef(source)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const dragStart = useRef<{ pointerId: number; x: number; y: number; crop: CoverCrop } | null>(
    null,
  )
  const [picture, setPicture] = useState<LoadedPicture | null>(null)
  const [crop, setCrop] = useState(INITIAL_COVER_CROP)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    let loadedUrl: string | null = null
    loadPicture(sourceRef.current).then(
      (loaded) => {
        if (cancelled) {
          URL.revokeObjectURL(loaded.url)
          return
        }
        loadedUrl = loaded.url
        setPicture(loaded)
      },
      () => {
        if (cancelled) return
        toast.error(m.coverAdjustFailed())
        closeRef.current()
      },
    )
    return () => {
      cancelled = true
      if (loadedUrl) URL.revokeObjectURL(loadedUrl)
    }
  }, [])

  const natural = picture && {
    width: picture.image.naturalWidth,
    height: picture.image.naturalHeight,
  }
  const box = natural && coverCropBox(natural, crop)

  const handlePointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (dragStart.current) return
    event.currentTarget.setPointerCapture(event.pointerId)
    dragStart.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, crop }
  }

  const handlePointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const start = dragStart.current
    if (!start || start.pointerId !== event.pointerId || !natural) return
    const frame = frameRef.current?.getBoundingClientRect()
    if (!frame?.width || !frame.height) return
    setCrop(
      dragCoverCrop(
        natural,
        start.crop,
        (event.clientX - start.x) / frame.width,
        (event.clientY - start.y) / frame.height,
      ),
    )
  }

  const handlePointerUp = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (dragStart.current?.pointerId === event.pointerId) dragStart.current = null
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!natural) return
    const shares: Record<string, [number, number]> = {
      ArrowLeft: [KEY_SHARE, 0],
      ArrowRight: [-KEY_SHARE, 0],
      ArrowUp: [0, KEY_SHARE],
      ArrowDown: [0, -KEY_SHARE],
    }
    const share = shares[event.key]
    if (!share) return
    event.preventDefault()
    setCrop(dragCoverCrop(natural, crop, share[0], share[1]))
  }

  const apply = async () => {
    if (!picture) return
    setSaving(true)
    try {
      onApply(await croppedCover(picture.image, crop))
      onClose()
    } catch {
      toast.error(m.coverAdjustFailed())
      setSaving(false)
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{m.coverAdjust()}</DialogTitle>
          <DialogDescription>{m.coverAdjustDescription()}</DialogDescription>
        </DialogHeader>

        <button
          ref={frameRef}
          type="button"
          aria-label={m.coverAdjustFrame()}
          style={{ aspectRatio: COVER_ASPECT }}
          className="squircle relative block w-full cursor-grab touch-none select-none overflow-hidden rounded-lg bg-muted outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 active:cursor-grabbing"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onLostPointerCapture={handlePointerUp}
          onKeyDown={handleKeyDown}
        >
          {picture && natural && box ? (
            <img
              src={picture.url}
              alt=""
              draggable={false}
              className="pointer-events-none absolute max-w-none"
              style={{
                width: `${(natural.width / box.width) * 100}%`,
                height: `${(natural.height / box.height) * 100}%`,
                left: `${(-box.left / box.width) * 100}%`,
                top: `${(-box.top / box.height) * 100}%`,
              }}
            />
          ) : null}
        </button>

        <Slider
          thumbLabel={m.coverAdjustZoom()}
          min={1}
          max={COVER_MAX_ZOOM}
          step={0.01}
          value={[crop.zoom]}
          disabled={!picture}
          onValueChange={([zoom]) => setCrop((current) => ({ ...current, zoom }))}
        />

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {m.cancel()}
          </Button>
          <Button type="button" onClick={apply} disabled={!picture || saving}>
            {m.coverAdjustApply()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Opens the cover adjust dialog on an images section's current cover. */
export function CoverAdjustButton({ cover }: { cover: CoverControls }) {
  const [open, setOpen] = useState(false)
  if (!cover.preview) return null
  return (
    <>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Crop className="h-4 w-4" />
        {m.coverAdjust()}
      </Button>
      {open ? (
        <CoverAdjustDialog
          source={cover.file ?? cover.preview}
          onApply={cover.replace}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  )
}
